/**
 * UnlinkedTanksPanel — #60 / DEC-365 (D10 amended): after a site change, a captured tank with
 * no same-name tank at the new site is left unlinked (its readings and name kept). The phone
 * cannot edit a saved run ticket, so the office picks which tank at the job's current site it
 * was, here (job detail and In Review).
 *
 * Lists only the rows the latest site change left unlinked (`unlinkedTankNames`, from the job
 * detail's siteChangeTankOutcome) that are still unlinked — not tanks a driver typed by hand at
 * a site with no registered tanks. Renders nothing when there are none.
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { invalidateAfterSiteChange, readSiteChangeError, useSiteOptions } from './site-change-api';

export interface UnlinkedTanksPanelProps {
  readonly jobId: string;
  /** siteChangeTankOutcome.unlinkedTankNames from GET /v1/jobs/:id (empty = render nothing). */
  readonly unlinkedTankNames: ReadonlyArray<string>;
}

const selectStyle: React.CSSProperties = {
  flex: 1,
  padding: '4px 8px',
  fontSize: '13px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

/** Nothing (and no query) unless the latest site change left a tank to pick. */
export function UnlinkedTanksPanel(props: UnlinkedTanksPanelProps): React.JSX.Element | null {
  return props.unlinkedTankNames.length === 0 ? null : <UnlinkedTanksPicker {...props} />;
}

function UnlinkedTanksPicker({
  jobId,
  unlinkedTankNames,
}: UnlinkedTanksPanelProps): React.JSX.Element | null {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const options = useSiteOptions(apiOpts, jobId, '', true);
  const [choice, setChoice] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<string | null>(null);

  const mutation = useMutation<unknown, Error, { tankRowId: string; haulerTankId: string }>({
    mutationFn: async ({ tankRowId, haulerTankId }) => {
      const res = await authedFetch(apiOpts, `/v1/jobs/${jobId}/capture-tanks/${tankRowId}/link`, {
        method: 'POST',
        body: JSON.stringify({ haulerTankId }),
      });
      if (!res.ok) throw new Error(await readSiteChangeError(res));
      return res.json() as Promise<unknown>;
    },
    onSuccess: () => {
      invalidateAfterSiteChange(queryClient, jobId);
      toast.success('Tank linked.');
    },
    onError: (err) => setError(err.message),
  });

  const unlinked = (options.data?.captureTanks ?? []).filter(
    (t) => t.haulerTankId === null && unlinkedTankNames.includes(t.tankName),
  );
  if (unlinked.length === 0) return null;
  const tanks = options.data?.tanks ?? [];

  return (
    <section
      data-testid="unlinked-tanks"
      style={{
        border: '1px solid var(--color-warning)',
        borderRadius: '6px',
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      <p style={{ fontSize: '13px', margin: 0, color: 'var(--color-text-primary)' }}>
        {unlinked.length === 1
          ? 'One tank on the run ticket is not linked to a tank at this site.'
          : `${String(unlinked.length)} tanks on the run ticket are not linked to a tank at this site.`}{' '}
        The readings are kept; pick which tank each one was.
      </p>
      {tanks.length === 0 && (
        <p style={{ fontSize: '12px', margin: 0, color: 'var(--color-text-tertiary)' }}>
          This site has no active tanks to pick from.
        </p>
      )}
      {tanks.length > 0 &&
        unlinked.map((row) => (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', minWidth: '90px' }}>{row.tankName}</span>
            <select
              aria-label={`Tank for ${row.tankName}`}
              value={choice[row.id] ?? ''}
              onChange={(e): void => setChoice((c) => ({ ...c, [row.id]: e.target.value }))}
              style={selectStyle}
            >
              <option value="">Pick a tank…</option>
              {tanks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              disabled={(choice[row.id] ?? '') === '' || mutation.isPending}
              onClick={(): void => {
                const haulerTankId = choice[row.id];
                if (haulerTankId === undefined || haulerTankId === '') return;
                setError(null);
                mutation.mutate({ tankRowId: row.id, haulerTankId });
              }}
            >
              Link
            </Button>
          </div>
        ))}
      {error !== null && (
        <p role="alert" style={{ fontSize: '12px', margin: 0, color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
    </section>
  );
}
